/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Inject, Input, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import * as Highcharts from 'highcharts';
import * as _ from 'lodash';

import {ReachEstimateApiService} from './reach-estimate-api.service';
import template from './reach-analysis-page.component.html';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {NetworkForecastApiService} from './network-forecast-api.service';
import {AppSharedModule} from '../common/app-shared.module';
import {TargetingModule} from '../targeting/targeting.module';

@Component({
  standalone: true,
  imports: [AppSharedModule, TargetingModule],
  selector: 'adn-reach-analysis-page',
  template
})
export class ReachAnalysisPageComponent implements OnInit, OnDestroy {
  @Input() model: any;
  @Input() isNew: boolean;

  @ViewChild('requestsChart')
  set requestsChartContainer(container: ElementRef<HTMLElement>) {
    this.requestsChartContainerRef = container;
    this.renderRequestsChart();
  }

  @ViewChild('samplesChart')
  set samplesChartContainer(container: ElementRef<HTMLElement>) {
    this.samplesChartContainerRef = container;
    this.renderSamplesChart();
  }

  readonly copyHook = {
    initCompare: (): void => {
      this.compareReachAnalysis = true;
    }
  };

  compareReachAnalysis = false;
  showingCharts = false;
  isAxAdvertiser: boolean;
  isSelfService: boolean;
  canSeeCharts: boolean;
  diagnostics: any = {};

  private forecast: any;
  private requestsChartContainerRef: ElementRef<HTMLElement>;
  private samplesChartContainerRef: ElementRef<HTMLElement>;
  private requestsChart: Highcharts.Chart | null = null;
  private samplesChart: Highcharts.Chart | null = null;

  constructor(
    private readonly networkForecastApi: NetworkForecastApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly reachEstimateApi: ReachEstimateApiService,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.isAxAdvertiser = this.LocalUserPermissions.isAxAdvertiser();
    this.isSelfService = this.LocalUserPermissions.isSelfService();
    this.canSeeCharts = this.localUserProfile.canSeeReachCharts();

    if (!this.canSeeCharts) {
      return;
    }

    this.reachEstimateApi.getDiagnostics().subscribe((diagnostics) => {
      this.ngZone.run(() => {
        this.diagnostics = diagnostics || {};
        this.renderSamplesChart();
      });
    });

    this.networkForecastApi.get().subscribe((forecast) => {
      this.ngZone.run(() => {
        this.forecast = forecast;
        this.renderRequestsChart();
      });
    });
  }

  ngOnDestroy(): void {
    this.requestsChart?.destroy();
    this.samplesChart?.destroy();
    this.requestsChart = null;
    this.samplesChart = null;
  }

  get availableSamplesPercentage(): number {
    const predictedRequests = this.diagnostics?.predictedRequests;
    return predictedRequests ? 100 * this.diagnostics.availableSamples / predictedRequests : 0;
  }

  private renderSamplesChart(): void {
    if (!this.samplesChartContainerRef?.nativeElement || !this.diagnostics?.chunks) {
      return;
    }

    this.samplesChart?.destroy();
    this.samplesChart = Highcharts.chart(this.samplesChartContainerRef.nativeElement, {
      chart: {type: 'column'},
      title: {text: 'Samples'},
      xAxis: {categories: _.map(this.diagnostics.chunks, 'time')},
      yAxis: {title: {text: 'Samples'}, min: 0},
      series: [{
        type: 'column',
        name: 'Available Samples',
        data: _.map(this.diagnostics.chunks, 'availableSamples')
      }]
    });
  }

  private renderRequestsChart(): void {
    if (!this.requestsChartContainerRef?.nativeElement || !this.forecast?.chunks) {
      return;
    }

    this.requestsChart?.destroy();
    this.requestsChart = Highcharts.chart(this.requestsChartContainerRef.nativeElement, {
      chart: {type: 'line'},
      title: {text: 'Network Traffic'},
      xAxis: {
        categories: _.map(this.forecast.chunks, (chunk: any) => chunk.dayOfWeek + ' ' + chunk.time)
      },
      yAxis: {title: {text: 'Requests'}},
      series: [{
        type: 'line',
        name: 'Observed Last Week',
        data: _.map(this.forecast.chunks, 'requestsLastWeek')
      }, {
        type: 'line',
        name: 'Predicted Next Week',
        data: _.map(this.forecast.chunks, 'requestsNextWeek')
      }]
    });
  }
}
